import TiltifySetup from '@components/overlays/social/tiltify/tiltify-setup.jsx';
import useTwitchConfig from '@hooks/useTwitchConfig.js';
import useTiltifyConfig from '@hooks/useTiltifyConfig.js';
import { useCallback, useEffect, useState } from 'preact/hooks';
import TwitchSetup from '@components/overlays/social/twitch/twitch-setup.jsx';
import {
  useDeepSignalFromLocalStorage,
  useSignalWithLocalStorage,
} from '@util/deepSignals.js';
import { deepSignal } from 'deepsignal';

import TiltifyCampaignSelector from '@components/overlays/social/tiltify/tiltify-campaign-selector.jsx';
import LinkSetupStep from '@components/overlays/social/link-setup/link-setup-step.jsx';
import TwitchChatbotActive from '@components/chatbots/twitch/components/twitch-chatbot-active.jsx';
import TwitchChatbotSettings from '@components/chatbots/twitch/components/twitch-chatbot-settings.jsx';
import TwitchChatbotConnecting from '@components/chatbots/twitch/components/twitch-chatbot-connecting.jsx';

import {
  useGetUserCampaign,
  useRemoveUserCampaign,
  useSetUserCampaign,
  useUpdateTiltifyConfigFromCampaign,
} from '@components/chatbots/twitch/data/useCampaigns.js';
import { useGetWebhooks } from '@components/chatbots/twitch/data/useGetWebhooks.js';
import {
  useJoinChannel,
  useLeaveChannel,
} from '@components/chatbots/twitch/data/useChannels.js';
import {
  useTiltifyCampaignConfigValidator,
  useTiltifyUserConfigValidator,
} from '@hooks/useTiltifyConfigValidator.js';
import { CheckCircle, ExclamationCircle } from 'flowbite-react-icons/solid';
import toast from 'react-hot-toast';
import { UpdateUserData } from '@util/analyticsHelper.js';

import twitchScreenshot from '@assets/chatbots/screenshots/twitch-screenshot.jpg';
import useTiltifyCampaign from '@hooks/useTiltifyCampaign.js';

const config = deepSignal({
  donationOnOff: 'yes',
  timerOptions: '5',
  milestoneMessages: 'yes',
  donationMessageType: 'default',
});

/** @typedef {object} Campaign
 * @property {string} donationTimer
 * @property {boolean} isFake
 * @property {string} tiltifyCampaignID
 * @property {string} tiltifyCampaignName
 * @property {string} tiltifyCampaignSlug
 * @property {string} tiltifyUsername
 * @property {string} tiltifyUserSlug
 * @property {string} twitchUsername
 */

const redirectURI =
  import.meta.env.VITE_TWITCH_CHATBOT_REDIRECT_URI ||
  `https://${window.location.hostname}/chatbot-landing`;

const lambdaURI = `${import.meta.env.VITE_BOT_URL}`;

export default function TwitchChatbotSetup() {
  useDeepSignalFromLocalStorage(config);

  const [disableConnectionButton, setDisableConnectionButton] = useState(false);
  const [chatbotConnecting, setChatbotConnecting] = useState(true);
  const [chatbotConnected, setChatbotConnected] = useState(false);
  const [twitchTokenValidated, setTwitchTokenValidated] = useState(false);
  const [visibleStep, setVisibleStep] = useState(1);

  const setConfigState = useSignalWithLocalStorage(config);
  const twitchConfig = useTwitchConfig();
  const tiltifyConfig = useTiltifyConfig();

  const validTiltifyUserConfig = useTiltifyUserConfigValidator();
  const validTiltifyCampaignConfig = useTiltifyCampaignConfigValidator();

  const validTwitchConfig = useCallback(() => {
    return (
      twitchConfig.twitchAccessToken !== null &&
      twitchConfig.twitchAccessToken.length > 0
    );
  }, [twitchConfig.twitchAccessToken]);

  const tiltifyDidSignIn = ({ userID }) => {
    if (userID && visibleStep === 1) {
      if (validTwitchConfig()) setVisibleStep(3);
      else setVisibleStep(2);
    }
  };

  const tiltifyDidSignOut = () => {
    tiltifyConfig.tiltifyUserID = null;
    twitchDidSignOut();
    setVisibleStep(1);
  };

  const { campaign } = useTiltifyCampaign({
    campaignID: tiltifyConfig.tiltifyCampaignID,
  });

  const onCampaignSelected = useCallback(
    ({ campaignSlug }) => {
      if (campaignSlug && visibleStep === 3) setVisibleStep(4);
    },
    [visibleStep]
  );

  const twitchDidSignIn = ({ username, accessToken, refreshToken }) => {
    twitchConfig.twitchUsername = username;
    twitchConfig.twitchAccessToken = accessToken;
    twitchConfig.twitchRefreshToken = refreshToken;

    if (refreshToken && accessToken && username) {
      setTwitchTokenValidated(true);

      if (validTiltifyUserConfig()) setVisibleStep(3);
    }

    UpdateUserData();
  };

  const twitchDidSignOut = () => {
    twitchConfig.twitchAccessToken = null;
    twitchConfig.twitchRefreshToken = null;
    twitchConfig.twitchUsername = null;

    if (tiltifyConfig.tiltifyUserID !== null) setVisibleStep(2);
    else setVisibleStep(1);
  };

  const { twitchUsername, twitchAccessToken } = twitchConfig;

  const getUserCampaign = useGetUserCampaign({
    twitchUsername,
    twitchAccessToken,
    twitchTokenValidated,
  });

  const getWebhooks = useGetWebhooks({
    twitchUsername,
    twitchAccessToken,
    twitchTokenValidated,
    setChatbotConnected,
    setChatbotConnecting,
  });

  const setUserCampaign = useSetUserCampaign({
    config,
    tiltifyConfig,
    twitchConfig,
  });

  const leaveChannel = useLeaveChannel({
    twitchUsername,
    twitchAccessToken,
  });

  const joinChannel = useJoinChannel({
    twitchUsername,
    twitchAccessToken,
    twitchTokenValidated,
  });

  const removeUserCampaign = useRemoveUserCampaign(twitchConfig);

  function getToastContent(type, message) {
    if (type === 'error') {
      return (
        <>
          <ExclamationCircle />
          <span>{message}</span>
        </>
      );
    }

    return (
      <>
        <CheckCircle />
        <span>{message}</span>
      </>
    );
  }

  function buildChatbotToast(type, message) {
    return toast(getToastContent(type, message), {
      position: 'top-center',
      className: `alert-${type} text-white`,
    });
  }

  function disconnectChatbot() {
    setDisableConnectionButton(true);
    setChatbotConnecting(false);
    leaveChannel()
      .then(() => removeUserCampaign())
      .then((result) => {
        setChatbotConnected(!result);
        setDisableConnectionButton(false);
        setVisibleStep(4);

        if (!result) buildChatbotToast('error', 'Failed to disconnect chatbot');
        else buildChatbotToast('success', 'Disconnected chatbot');
      });
  }

  function connectChatbot() {
    setChatbotConnecting(true);
    setDisableConnectionButton(true);
    setUserCampaign()
      .then((joined) => {
        if (joined) return joinChannel();
        return { success: false };
      })
      .then((result) => {
        setChatbotConnected(result.success);
        setDisableConnectionButton(false);
        setChatbotConnecting(false);
        setVisibleStep(5);

        if (!result) buildChatbotToast('error', 'Failed to connect chatbot');
        else buildChatbotToast('success', 'Connected chatbot');
      });
  }

  const updateTiltifyConfigFromCampaign = useUpdateTiltifyConfigFromCampaign(
    onCampaignSelected,
    tiltifyConfig
  );

  useEffect(() => {
    getUserCampaign.then((result) => {
      if (!result.success || chatbotConnected || !chatbotConnecting) return;
      const updatedKeys = [];
      const ignoreKeys = ['tiltifyCampaignGoal', 'tiltifyUserID'];

      const campaign = result.campaign;

      if (!campaign) {
        setChatbotConnecting(false);
        return;
      }

      updateTiltifyConfigFromCampaign(campaign);

      Object.keys(tiltifyConfig)
        .filter((key) => !ignoreKeys.includes(key))
        .forEach((key) => {
          if (tiltifyConfig[key] && campaign[key] !== tiltifyConfig[key]) {
            updatedKeys.push(key);
            campaign[key] = tiltifyConfig[key];
          }
        });

      const getWebhooksAndUpdate = () => {
        return getWebhooks().then((result) => {
          const { success, webhooks } = result;
          setChatbotConnecting(false);
          setChatbotConnected(
            !!webhooks && Object.keys(webhooks).length > 0 && success
          );

          setVisibleStep((current) => {
            if (current === 4) return 5;
            return current;
          });
        });
      };

      if (updatedKeys.length === 0) return getWebhooksAndUpdate();
      return setUserCampaign(campaign).then(() => getWebhooksAndUpdate());
    });
  }, [
    chatbotConnected,
    chatbotConnecting,
    getUserCampaign,
    getWebhooks,
    onCampaignSelected,
    setChatbotConnected,
    setChatbotConnecting,
    setUserCampaign,
    tiltifyConfig,
    updateTiltifyConfigFromCampaign,
    validTwitchConfig,
  ]);

  useEffect(() => {
    if (campaign && validTwitchConfig() && validTiltifyUserConfig()) {
      setVisibleStep(4);
    }
  }, [campaign, validTwitchConfig, validTiltifyUserConfig]);

  useEffect(() => {
    if (validTiltifyUserConfig()) {
      if (validTwitchConfig()) {
        if (validTiltifyCampaignConfig() && campaign !== null) {
          setVisibleStep(4);
        } else setVisibleStep(3);
      } else setVisibleStep(2);
    } else setVisibleStep(1);
  }, [
    campaign,
    validTwitchConfig,
    validTiltifyUserConfig,
    validTiltifyCampaignConfig,
    setVisibleStep,
  ]);

  return (
    <>
      <div>
        <img
          className="rounded-md w-full mb-6"
          src={`${twitchScreenshot}`}
          alt="Discord Screenshot"
        />
      </div>
      <div className="min-h-link-setup mb-4 pb-4">
        <LinkSetupStep
          step={1}
          currentStep={visibleStep}
          title={'Connect the Chatbot to Tiltify'}
        >
          <TiltifySetup
            onSignIn={tiltifyDidSignIn}
            onSignOut={tiltifyDidSignOut}
          />
        </LinkSetupStep>

        <LinkSetupStep
          step={2}
          currentStep={visibleStep}
          title={'Connect the Chatbot to Twitch'}
        >
          <TwitchSetup
            lambdaURI={lambdaURI}
            redirectURI={redirectURI}
            tokenValidated={(isValid) => setTwitchTokenValidated(isValid)}
            onSignOut={twitchDidSignOut}
            onSignIn={twitchDidSignIn}
          />
        </LinkSetupStep>

        <LinkSetupStep
          step={3}
          currentStep={visibleStep}
          title={'Select your Fundraising Campaign'}
        >
          <TiltifyCampaignSelector
            allowRetired={true}
            disabled={chatbotConnected && validTwitchConfig()}
            onCampaignSelected={onCampaignSelected}
          />
        </LinkSetupStep>

        <LinkSetupStep
          step={4}
          currentStep={visibleStep}
          title={'Confirm Chatbot Settings'}
        >
          {chatbotConnected ? (
            <TwitchChatbotActive
              disconnectChatbot={disconnectChatbot}
              disableConnectionButton={disableConnectionButton}
            />
          ) : chatbotConnecting ? (
            <TwitchChatbotConnecting />
          ) : (
            <TwitchChatbotSettings
              config={config}
              tiltifyCampaignID={tiltifyConfig.tiltifyCampaignID}
              hasLoadedCampaign={campaign !== null}
              isCampaignRetired={campaign?.status === 'retired'}
              chatbotConnected={chatbotConnected}
              setConfigState={setConfigState}
              connectChatbot={connectChatbot}
              disableConnectionButton={disableConnectionButton}
            />
          )}
        </LinkSetupStep>
      </div>
    </>
  );
}
